import { DocumentEdit, FileStack } from '@creditornot/cb-icons';
import React from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router';
import styled from 'styled-components';

import { OnboardingProgressResponse } from 'apis/merchant-onboarding-service/types';
import { useTelemetryViewEvent } from 'common/hooks';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { ssmoLandingPageInteractionClickStep, ssmoLandingPageView } from 'core/telemetry/avo/Avo';
import { SsmoTask } from 'modules/onboarding-page/constants';

import Header from './Header';
import OnboardingStages from './OnboardingStages/OnboardingStages';
import WhatsNextCard from './WhatsNextCard';
import { BASE_STEPS_PREVIEW_INTL_ID } from './constants';
import AccountingInvoice from './icons/AccountingInvoice';
import CheckDouble from './icons/CheckDouble';
import Stores from './icons/Stores';
import { Stage } from './types';
import { getDisabledTasks } from '../../utils';

type StepsPreviewProps = {
  firstName?: string;
  merchantId: string;
  onboardingProgress: OnboardingProgressResponse;
};

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-start: 48px;
  width: 680px;
  margin-block-end: 48px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-inline: 16px;
    width: 100%;
    margin-block-start: 28px;
  }
`;

const StepsPreview = ({ firstName, merchantId, onboardingProgress }: StepsPreviewProps) => {
  const { formatMessage } = useIntl();
  const { url } = useRouteMatch();
  const history = useHistory();

  useTelemetryViewEvent('ssmo_landing_page_view', {}, durationMs => ssmoLandingPageView({ durationMs }));

  const disabledTasks = getDisabledTasks(onboardingProgress);

  const stages: Stage[] = [
    {
      number: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.get-started.number` }),
      header: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.get-started.title` }),
      description: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.get-started.description` }),
      tasks: [
        {
          icon: <Stores />,
          title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.get-started.tasks.add-opening-times.title` }),
          duration: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.duration` }, { duration: 3 }),
          status: onboardingProgress.tasks.OPENING_TIMES.status,
          onStart: () => {
            ssmoLandingPageInteractionClickStep({ clickTarget: 'ssmo_opening_times' });
            history.push(`${url}/opening-hours`);
          },
          taskSteps: onboardingProgress.tasks.OPENING_TIMES.steps,
          type: SsmoTask.OPENING_TIMES,
          disabled: disabledTasks.has(SsmoTask.OPENING_TIMES),
        },
        {
          icon: <DocumentEdit />,
          title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.get-started.tasks.share-menu.title` }),
          duration: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.duration` }, { duration: 5 }),
          status: onboardingProgress.tasks.MENU_SUBMISSION.status,
          onStart: () => {
            ssmoLandingPageInteractionClickStep({ clickTarget: 'ssmo_menu' });
            history.push(`${url}/upload-menu`);
          },
          taskSteps: onboardingProgress.tasks.MENU_SUBMISSION.steps,
          type: SsmoTask.MENU_SUBMISSION,
          disabled: disabledTasks.has(SsmoTask.MENU_SUBMISSION),
        },
      ],
    },
    {
      number: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.number` }),
      header: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.title` }),
      description: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.description` }),
      tasks: [
        {
          icon: <FileStack />,
          title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.create-agreement.title` }),
          duration: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.duration` }, { duration: 10 }),
          status: onboardingProgress.tasks.CONTRACT_SIGNING.status,
          onStart: () => {
            ssmoLandingPageInteractionClickStep({ clickTarget: 'ssmo_create_agreement' });
            history.push(`${url}/contract-details`);
          },
          taskSteps: onboardingProgress.tasks.CONTRACT_SIGNING.steps,
          type: SsmoTask.CONTRACT_SIGNING,
          disabled: disabledTasks.has(SsmoTask.CONTRACT_SIGNING),
        },
        {
          icon: <CheckDouble />,
          title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.verify-identity.title` }),
          duration: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.duration` }, { duration: 5 }),
          status: onboardingProgress.tasks.KYC.status,
          onStart: () => {
            ssmoLandingPageInteractionClickStep({ clickTarget: 'ssmo_verify_identity' });
            history.push(`${url}/identity-verification`);
          },
          taskSteps: onboardingProgress.tasks.KYC.steps,
          type: SsmoTask.KYC,
          disabled: disabledTasks.has(SsmoTask.KYC),
        },
        {
          icon: <AccountingInvoice />,
          title: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.build-partnership.tasks.verify-business.title` }),
          duration: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.duration` }, { duration: 10 }),
          status: onboardingProgress.tasks.KYB.status,
          onStart: () => {
            ssmoLandingPageInteractionClickStep({ clickTarget: 'ssmo_verify_business' });
            history.push(`${url}/${merchantId}/business-verification`);
          },
          taskSteps: onboardingProgress.tasks.KYB.steps,
          type: SsmoTask.KYB,
          disabled: disabledTasks.has(SsmoTask.KYB),
        },
      ],
    },
    {
      number: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.number` }),
      header: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.title` }),
      description: formatMessage({ id: `${BASE_STEPS_PREVIEW_INTL_ID}.whats-next.description` }),
      tasks: [],
      customComponent: <WhatsNextCard />,
    },
  ];

  return (
    <ContentWrapper>
      <Header firstName={firstName} />
      <OnboardingStages stages={stages} />
    </ContentWrapper>
  );
};

export default StepsPreview;
